'use client'

import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  useDisclosure
} from '@nextui-org/react'
import { CircleXIcon } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'

import { MaintenanceModal, useMaintenance } from '@/components/ui/maintenance'
import { fetchPromotionDetail } from '@/libs/actions/common.action'
import { stripHtml } from '@/libs/utils'

import { PromotionBookingForm } from './promotionBookingForm'

// Interface to define promotion-specific constraints
interface PromotionConstraints {
  branchIds: string
  minGuests?: number
  maxGuests?: number
  advancebooking?: number
  isBuyOneGetOne?: boolean
  expired?: boolean
  expireDate?: string
  availableDateTimes?: Array<{
    start_date: string
    end_date: string
    start_time: string
    end_time: string
  }>
  cs_promotion_id?: number
}

interface PromotionButtonBookingProps {
  constraints: PromotionConstraints
  className?: string
  onBookingStart?: () => void
  onBookingEnd?: () => void
  enhanceWithDetailedData?: boolean
  promotionSlug?: string
  id?: string
  source?: string
}

export function PromotionButtonBooking({
  constraints,
  className,
  onBookingStart,
  onBookingEnd,
  enhanceWithDetailedData,
  promotionSlug,
  id,
  source
}: PromotionButtonBookingProps) {
  // __STATE's
  const t = useTranslations()
  const locale = useLocale()
  const { isOpen, onOpen, onOpenChange } = useDisclosure()
  const maintenance = useMaintenance()
  const [enhancedConstraints, setEnhancedConstraints] = useState(constraints)
  const [isEnhancing, setIsEnhancing] = useState(false)

  // Watch for modal close to call onBookingEnd
  useEffect(() => {
    if (!isOpen) {
      onBookingEnd?.()
    }
  }, [isOpen, onBookingEnd])

  // __FUNCTION's
  const enhancePromotionConstraints = useCallback(async () => {
    if (!enhanceWithDetailedData || !promotionSlug || isEnhancing) return

    setIsEnhancing(true)
    try {
      const detailedPromotionResponse = (await fetchPromotionDetail(locale, promotionSlug)) as any

      // Handle the response structure - it should have detail, listtime, etc.
      if (detailedPromotionResponse && detailedPromotionResponse.detail) {
        const detailedPromotion = {
          ...detailedPromotionResponse.detail,
          listtime: detailedPromotionResponse.listtime,
          branches: detailedPromotionResponse.branches,
          products: detailedPromotionResponse.products,
          expired: detailedPromotionResponse.expired
        }

        const hasSpecificDates =
          detailedPromotion.listtime && Array.isArray(detailedPromotion.listtime)
        const getListtimeArray = () => {
          if (!detailedPromotion.listtime) return []
          return Array.isArray(detailedPromotion.listtime)
            ? detailedPromotion.listtime
            : [detailedPromotion.listtime]
        }

        const calculateExpiration = () => {
          if (hasSpecificDates) {
            const listtimeArray = getListtimeArray()
            const latestDate = listtimeArray
              .map((item: any) => new Date(item.end_date))
              .reduce(
                (latest: Date, current: Date) => (current > latest ? current : latest),
                new Date(0)
              )
            return latestDate < new Date()
          }
          if (detailedPromotion?.listtime && !Array.isArray(detailedPromotion.listtime)) {
            return new Date(detailedPromotion.listtime.end_date) < new Date()
          }
          if (typeof detailedPromotion.expired === 'boolean') {
            return detailedPromotion.expired
          }
          return false
        }

        const getExpireDate = () => {
          if (hasSpecificDates) {
            const listtimeArray = getListtimeArray()
            const latestDate = listtimeArray
              .map((item: any) => item.end_date)
              .reduce(
                (latest: string, current: string) => (current > latest ? current : latest),
                ''
              )
            return latestDate || undefined
          }
          if (detailedPromotion?.listtime && !Array.isArray(detailedPromotion.listtime)) {
            return (
              detailedPromotion.products?.[0]?.expire_date ||
              detailedPromotion.listtime.end_date ||
              undefined
            )
          }
          return detailedPromotion.products?.[0]?.expire_date || undefined
        }

        // Create enhanced constraints
        const enhanced = {
          ...constraints,
          expired: calculateExpiration(),
          expireDate: getExpireDate(),
          availableDateTimes: hasSpecificDates ? getListtimeArray() : undefined
        }

        setEnhancedConstraints(enhanced)
      }
    } catch (error) {
      //console.error('Error enhancing promotion constraints:', error)
    } finally {
      setIsEnhancing(false)
    }
  }, [locale, enhanceWithDetailedData, promotionSlug, constraints, isEnhancing])

  const openBooking = useCallback(async () => {
    // Enhance constraints with detailed data if requested
    await enhancePromotionConstraints()

    // Blocks instead of opening: the form posts to the booking API that maintenance takes offline.
    if (await maintenance.check()) return

    onOpen()

    // Call the callback when (modal opens)
    onBookingStart?.()
  }, [maintenance.check, onOpen, onBookingStart, enhancePromotionConstraints])

  // __RENDER
  return (
    <>
      <button
        className={`btn btn-primary mx-auto h-10 px-8 md:h-12 ${className || ''}`}
        type='button'
        aria-label='Button Booknow'
        id={id}
        onClick={openBooking}>
        <span
          className='text z-10 text-2xl font-light uppercase leading-none tracking-widest'
          dangerouslySetInnerHTML={{ __html: stripHtml(t.raw('Menu.BOOKNOW_BUTTON')) }}
        />
      </button>

      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size='md'
        radius='sm'
        placement='auto'
        classNames={{
          wrapper: 'z-[9999]'
        }}>
        <ModalContent className='sm:max-w-2xl md:max-w-6xl'>
          {(onClose) => {
            return enhancedConstraints.expired ? (
              <ModalBody className='py-6'>
                <div className='grid gap-2 text-center'>
                  <CircleXIcon className='mx-auto size-20 stroke-1 text-red-500' />
                  <div className='text-xl font-semibold text-zinc-800'>Promotion Expired</div>
                  <p className='text-sm font-normal text-zinc-600'>
                    We're sorry, this promotion has expired and is no longer available for booking.
                  </p>
                </div>

                <Button
                  className='mx-auto w-32 bg-red-600 text-lg text-white'
                  size='sm'
                  radius='sm'
                  onPress={onClose}>
                  Close
                </Button>
              </ModalBody>
            ) : (
              <>
                <ModalHeader className='px-8 pt-8'>Promotion Booking</ModalHeader>

                <ModalBody className='gap-6 px-8 pb-8 pt-0 sm:grid'>
                  <PromotionBookingForm
                    key='.form-promotion-booking'
                    constraints={enhancedConstraints}
                    source={source}
                  />
                </ModalBody>
              </>
            )
          }}
        </ModalContent>
      </Modal>

      <MaintenanceModal
        isOpen={maintenance.isBlocked}
        message={maintenance.message}
        onClose={maintenance.close}
      />
    </>
  )
}
